import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import '../../assets/styles/events/ticketView.scss';
import usePageMeta from '../../services/page-meta';
import NotFoundPage from '../Errors/NotFound';
import {parseRouteResponse} from '../../services/route-response';

function getActionLabel(actionType) {
    if (actionType === 'activate') {
        return 'Підтвердити присутність';
    }

    if (actionType === 'finish') {
        return 'Завершити захід';
    }

    return 'Квиток було активовано';
}

export default function TicketViewPage() {
    const {t} = useTranslation();
    const {id, ticketId} = useParams();
    const [payload, setPayload] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const pageTitle = payload?.title || t('meta.conferenceTicket.title', 'Ticket verification');

    usePageMeta({
        title: pageTitle,
        description: t('meta.conferenceTicket.description', t('app.description')),
        keywords: t('meta.conferenceTicket.keywords', t('app.keywords')),
    });

    useEffect(() => {
        setIsLoading(true);

        fetch(`/api/v1/conference/${id}/tickets/${ticketId}`, {credentials: 'same-origin'})
            .then(async (response) => {
                const json = await response.json().catch(() => null);
                return {response, json};
            })
            .then(({response, json}) => {
                const {kind, redirectUrl} = parseRouteResponse(response, json);

                if (kind === 'not_found') {
                    setPayload(null);
                    setNotFound(true);
                    return;
                }

                setNotFound(false);

                if ((kind === 'unauthorized' || kind === 'redirect' || kind === 'error') && redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }

                if (kind === 'error') {
                    setPayload(null);
                    return;
                }

                if (json?.state === 'active') {
                    setPayload(json);
                    return;
                }

                setPayload(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id, ticketId]);

    const actionLabel = useMemo(() => getActionLabel(payload?.action?.type), [payload?.action?.type]);

    if (isLoading) {
        return null;
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (!payload) {
        return null;
    }

    return (
        <section className="spa-ticket-view">
            <div className="container">
                <div className="portfolio-header flex-center">
                    <div className="primary-actions"><a href={payload.links?.home || '/'}>На головну</a></div>
                    <h1>{pageTitle}</h1>
                </div>

                <div className="portfolio-wrap">
                    <div className="portfolio-header">
                        <div>{payload.ticket?.conferenceName || ''}</div>
                    </div>
                    <div className="portfolio-header avatar-header">
                        <img src={payload.user?.avatarUrl || '/img/no-photo.svg'} alt="" />
                    </div>
                    <div className="portfolio-section">
                        <div className="portfolio-row">
                            <div className="portfolio-row-data-label">Прізвище, ім'я, по батькові</div>
                            <div className="portfolio-row-data name">{payload.user?.fullName || ''}</div>
                        </div>
                        <div className="portfolio-row">
                            <div className="portfolio-row-data-label">Тип квитка</div>
                            <div className="portfolio-row-data">{payload.ticket?.ticketName || ''}</div>
                        </div>
                        <div className="portfolio-row">
                            <div className="portfolio-row-data-label">Дата початку заходу</div>
                            <div className="portfolio-row-data">{payload.ticket?.conferenceDateStart || ''}</div>
                        </div>
                        <div className="portfolio-row">
                            <div className="portfolio-row-data-label">Дата закінчення заходу</div>
                            <div className="portfolio-row-data">{payload.ticket?.conferenceDateEnd || ''}</div>
                        </div>
                    </div>
                    <div className="portfolio-footer">
                        <form className="portfolio-footer-section" method="POST" action={payload.action?.url || '#'}>
                            <button
                                style={{width: '20.625rem'}}
                                className="btn"
                                type="submit"
                                disabled={!payload.action?.enabled}>
                                {actionLabel}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
